import React, { useEffect, useState, useRef } from 'react';
// @ts-ignore
import styles from './Navbar.module.css';
import Logo from '../Logo/Logo';
import { LOGIN_PAGE } from 'settings/constant';
import { useNavigate, useLocation } from 'react-router-dom';
import BookingService from 'service/BookingService';
import HostProfileService from 'service/HostProfileService';

interface UserDetails {
  firstName: string;
  roles?: any;
  role?: any;
  authorities?: any;
  userType?: string;
  type?: string;
  isAdmin?: boolean;
  planType?: string;
}

const AVATAR_COLORS = ['#B8860B', '#7B6F8B', '#4A8FA8', '#6B8E6B', '#A0522D'];

// Mirrors ManageHostPlans.tsx's normalizePlanTier / backend PlanType.fromValue() — a host's
// stored planType can still be a pre-consolidation legacy value (basic/advanced/custom) if it
// hasn't been touched since the Free/Premium merge, even though every other premium check in
// the app already treats those as Premium. Without this, such a host's "Host Pro" tag and
// "Host Profile" nav link silently never show up.
const isPremiumPlanType = (raw?: string): boolean =>
  raw === 'premium' || raw === 'basic' || raw === 'advanced' || raw === 'custom';

const getAvatarColor = (name: string): string => {
  const idx = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
};

const Navbar: React.FC<{ minimal?: boolean }> = ({ minimal = false }) => {
  const [userName, setUserName] = useState<string>('');
  const [planType, setPlanType] = useState<string>('none');
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hostDetails = localStorage.getItem('userDetails');
    if (!hostDetails) return;
    const parsed: UserDetails = JSON.parse(hostDetails);
    setLoggedIn(true);
    setUserName(parsed.firstName || '');
    // Paint instantly from whatever's cached, then verify against the live plan — this
    // localStorage snapshot is only ever written at login (or right after a self-serve Zoho
    // purchase). A plan change made any other way (e.g. an admin setting it directly) never
    // updates a device's existing cached snapshot, so a session that logged in before that
    // change keeps showing the old plan indefinitely — which is why the "Host Pro" tag can
    // show correctly on one device/browser (logged in after the change) and not another
    // (logged in before it), even against the same DB.
    setPlanType(parsed.planType || 'none');
    if (localStorage.getItem('jwtToken')) {
      HostProfileService.getMyChatAccessTier()
        .then((tier) => {
          setPlanType(tier);
          try {
            localStorage.setItem('userDetails', JSON.stringify({ ...parsed, planType: tier }));
          } catch {}
        })
        .catch(() => {});
    }
  }, []);

  // Independent of the generic `isAdmin` flag above — a Soulpresso-only admin
  // (e.g. SOULPRESSO_ADMIN_USERS who isn't in ADMIN_USERS) won't have isAdmin
  // set, but should still see the Soulpresso Bookings link.
  const [showSoulpressoLink, setShowSoulpressoLink] = useState<boolean>(false);

  useEffect(() => {
    const hostDetails = localStorage.getItem('userDetails');
    if (!hostDetails) return;
    BookingService.getBookingAdminAccess()
      .then((res) => setShowSoulpressoLink(!!res?.isBysAdmin || !!res?.isSoulpressoAdmin))
      .catch(() => {});
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const logOut = (e: React.MouseEvent): void => {
    e.stopPropagation();
    localStorage.clear();
    navigate(LOGIN_PAGE);
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      profileRef.current &&
      !profileRef.current.contains(event.target as Node)
    ) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isAdminFromRoles = (roles: any): boolean => {
    const arr = Array.isArray(roles) ? roles : roles ? [roles] : [];
    return arr
      .map((r: any) => String(r || '').toUpperCase())
      .some((r: string) => r === 'ADMIN' || r === 'ROLE_ADMIN' || r.includes('ADMIN'));
  };

  const isAdmin = (() => {
    try {
      const raw = localStorage.getItem('userDetails');
      const parsed = raw ? (JSON.parse(raw) as UserDetails) : null;
      return (
        parsed?.isAdmin === true ||
        isAdminFromRoles(parsed?.roles) ||
        isAdminFromRoles(parsed?.role) ||
        isAdminFromRoles(parsed?.authorities) ||
        String(parsed?.userType || parsed?.type || '').toUpperCase().includes('ADMIN')
      );
    } catch (_) {
      return false;
    }
  })();

  const avatarLetter = userName ? userName[0].toUpperCase() : '?';
  const avatarColor = userName ? getAvatarColor(userName) : '#888';

  // All nav items rendered identically on desktop nav row and inside the
  // mobile drawer so both surfaces stay in sync.
  const navItems: { label: string; path: string; matchPrefix?: string }[] = [
    { label: 'Home', path: '/' },
    { label: 'Chats', path: '/messages' },
    {
      label: 'Calendar',
      path: '/host/calendar',
      matchPrefix: '/host/calendar',
    },
    { label: 'Bookings', path: '/host/myBookings' },
    { label: 'Bids', path: '/host/my-bids' },
    { label: 'Deals', path: '/host/deals' },
    { label: 'Pricing Plans', path: '/pricing-plan' },
    // An admin's own listings are plan free, so there is nothing to upgrade
    ...(isAdmin ? [] : [{ label: 'Active Plans', path: '/host/active-plans' }]),
    // Only Premium hosts have a guest-facing profile to edit / a Host Pro listing to see
    // analytics for. This is only a UX hint — Analytics itself does the real per-property check.
    ...(isPremiumPlanType(planType)
      ? [
          { label: 'Host Profile', path: '/host/profile' },
          { label: 'Analytics', path: '/host/analytics' },
        ]
      : []),
  ];

  const isItemActive = (item: { path: string; matchPrefix?: string }) => {
    if (item.matchPrefix) return location.pathname.startsWith(item.matchPrefix);
    return isActive(item.path);
  };

  const goTo = (path: string) => {
    setShowMenu(false);
    navigate(path);
  };

  return (
    <div className={styles.navbar}>
      {/* Logo */}
      <div className={styles.logoWrap}>
        <Logo
          withLink
          linkTo="/"
          src="https://bid-your-stay.s3.ap-south-1.amazonaws.com/assets/images/logo/unpaqd_logo.svg"
          title="Unpaqd"
        />
      </div>

      {/* Nav links + avatar */}
      {loggedIn && !minimal && (
        <div className={styles.rightSection}>
          <nav className={styles.navLinks}>
            {navItems.map((item) => (
              <button
                key={item.path}
                className={`${styles.navLink} ${
                  isItemActive(item) ? styles.navLinkActive : ''
                }`}
                onClick={() => goTo(item.path)}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Avatar */}
          <div className={styles.profileMenuContainer} ref={profileRef}>
            <div
              className={styles.avatar}
              style={{ background: avatarColor }}
              onClick={() => setShowMenu((s) => !s)}
            >
              {avatarLetter}
            </div>

            {showMenu && (
              <div ref={menuRef} className={styles.menu}>
                <div className={styles.menuHeader} style={{ alignItems: isPremiumPlanType(planType) ? 'flex-start' : 'center' }}>
                  <div className={styles.menuAvatar} style={{ background: avatarColor }}>
                    {avatarLetter}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span className={styles.menuName}>{userName}</span>
                    {isPremiumPlanType(planType) && (
                      <span
                        style={{
                          alignSelf: 'flex-start',
                          fontSize: '10px',
                          fontWeight: 700,
                          lineHeight: 1,
                          letterSpacing: '0.4px',
                          textTransform: 'uppercase',
                          color: '#FF710F',
                          background: 'rgba(255, 113, 16, 0.12)',
                          border: '1px solid rgba(255, 113, 16, 0.35)',
                          borderRadius: '999px',
                          padding: '3px 7px',
                        }}
                      >
                        Host Pro
                      </span>
                    )}
                  </div>
                </div>
                <hr className={styles.menuDivider} />
                {showSoulpressoLink && (
                  <>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/booking-price-breakup'); }}
                    >
                      Soulpresso Bookings
                    </button>
                    <hr className={styles.menuDivider} />
                  </>
                )}
                {/* Mirrors navLinks (hidden below 768px, see .navLinks media query) so every
                    primary destination stays reachable from this one menu on mobile — there's
                    no separate mobile drawer anymore. Hidden on desktop (.mobileOnlyMenuGroup)
                    since .navLinks already shows these there. */}
                <div className={styles.mobileOnlyMenuGroup}>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/'); }}
                  >
                    Home
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/messages'); }}
                  >
                    Chats
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/host/calendar'); }}
                  >
                    Calendar
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/host/myBookings'); }}
                  >
                    Bookings
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/host/my-bids'); }}
                  >
                    Bids
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/host/deals'); }}
                  >
                    Deals
                  </button>
                  <button
                    className={styles.menuItem}
                    onClick={() => { setShowMenu(false); navigate('/pricing-plan'); }}
                  >
                    Pricing Plans
                  </button>
                  {!isAdmin && (
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/host/active-plans'); }}
                    >
                      Active Plans
                    </button>
                  )}
                  <hr className={styles.menuDivider} />
                </div>
                {/* Available to every logged-in host — not admin-gated. Hosts can unsync
                    their own property; admins can unsync any property (enforced server-side). */}
                <button
                  className={styles.menuItem}
                  onClick={() => { setShowMenu(false); navigate('/host/unsync-airbnb-property'); }}
                >
                  Unsync Airbnb Pricing
                </button>
                <button
                  className={styles.menuItem}
                  onClick={() => { setShowMenu(false); navigate('/host/gst-details'); }}
                >
                  GST Details
                </button>
                <button
                  className={styles.menuItem}
                  onClick={() => { setShowMenu(false); navigate('/host/calculator'); }}
                >
                  Pricing Calculator
                </button>
                <button
                  className={styles.menuItem}
                  onClick={() => { setShowMenu(false); navigate('/host/pricelabs-sync'); }}
                >
                  PriceLabs Onboarding
                </button>
                <hr className={styles.menuDivider} />
                {isAdmin && (
                  <>
                    {/* <div className={styles.menuSectionLabel}>Admin</div> */}
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/pending-approval'); }}
                    >
                      Pending Approvals
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/manage-property'); }}
                    >
                      Manage Property
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/activate-property'); }}
                    >
                      Activate Property
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/generate-voucher'); }}
                    >
                      Generate Voucher
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/generate-brochure'); }}
                    >
                      Generate Brochure
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/brochures'); }}
                    >
                      Brochures (automatic)
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/unpaqd-reviews'); }}
                    >
                      Unpaqd Reviews
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/review-stats'); }}
                    >
                      Review Stats
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/perfect-stay-feedback'); }}
                    >
                      Perfect Stay Feedback
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/manage-host-plans'); }}
                    >
                      Manage Host Plans
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/unique-stays'); }}
                    >
                      Unique Stay Tags
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/send-notification'); }}
                    >
                      Send Notification
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/assign-property-host'); }}
                    >
                      Assign Property to Host
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/unassign-property-host'); }}
                    >
                      Unassign Property from Host
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/walkthrough-requests'); }}
                    >
                      Walkthrough Requests
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/lower-price-requests'); }}
                    >
                      Lower Price Requests
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/hotel-rate-requests'); }}
                    >
                      Hotel Rate Requests
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/host-financial-details'); }}
                    >
                      Host Financial Details
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/hotel-bookings'); }}
                    >
                      Hotel Bookings
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/leads'); }}
                    >
                      Leads Dashboard
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/property-source'); }}
                    >
                      Property Source (B2B/B2C)
                    </button>
                    <button
                      className={styles.menuItem}
                      onClick={() => { setShowMenu(false); navigate('/admin/sync-failures'); }}
                    >
                      Sync Failures
                    </button>
                    <hr className={styles.menuDivider} />
                  </>
                )}
                <button className={`${styles.menuItem} ${styles.menuItemLogout}`} onClick={logOut}>
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Navbar;
